import { createApi } from '@reduxjs/toolkit/query/react';
import deserialize from 'helpers/getroPropertyMapper';
import { networkBaseQueryV2, axiosBaseQuery } from '../queries';
import { contactsApi } from '../contacts';

// Public API for unauthenticated signup form endpoints
export const publicSignupFormsApi = createApi({
  reducerPath: 'publicSignupFormsApi',
  baseQuery: (args) => axiosBaseQuery({ ...args, version: 'v2' }),
  tagTypes: ['public-signup-form'],
  endpoints: (builder) => ({
    getPublicSignupForm: builder.query({
      query: ({ slug }) => ({
        url: `/shared_lists/${slug}/signup_form`,
      }),
      transformResponse: (response) => deserialize(response),
      providesTags: (_, __, { slug }) => [{ type: 'public-signup-form', id: slug }],
    }),

    submitPublicSignupForm: builder.mutation({
      query: ({ slug, ...data }) => ({
        url: `/shared_lists/${slug}/signup`,
        method: 'POST',
        data,
      }),
    }),
  }),
});

// Authenticated API for admin signup form management
export const signupFormsApi = createApi({
  reducerPath: 'signupFormsApi',
  baseQuery: networkBaseQueryV2,
  tagTypes: ['signup-form'],
  endpoints: (builder) => ({
    getSignupForm: builder.query({
      query: ({ collectionId, sharedListId }) => ({
        url: `/shared_lists/${sharedListId}/signup_forms`,
        networkId: collectionId,
      }),
      transformResponse: (response) => deserialize(response),
      providesTags: (_, __, { sharedListId }) => [{ type: 'signup-form', id: sharedListId }],
    }),

    createSignupForm: builder.mutation({
      query: ({ collectionId, sharedListId }) => ({
        url: `/shared_lists/${sharedListId}/signup_forms`,
        method: 'POST',
        data: {
          title: 'Join our talent network',
          description: '',
          active: true,
          fields: [],
        },
        networkId: collectionId,
      }),
      transformResponse: (response) => deserialize(response),
      invalidatesTags: (_, __, { sharedListId }) => [{ type: 'signup-form', id: sharedListId }],
      async onQueryStarted({ sharedListId }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          contactsApi.util.updateQueryData('getSharedList', { listId: sharedListId.toString() }, (draft) => {
            if (draft?.sharedList) {
              Object.assign(draft.sharedList, { signupFormEnabled: true });
            }
          }),
        );

        try {
          await queryFulfilled;
          dispatch(contactsApi.util.invalidateTags(['contacts-shared-list']));
        } catch {
          patchResult.undo();
        }
      },
    }),
    updateSignupForm: builder.mutation({
      query: ({ collectionId, sharedListId, formId, ...data }) => ({
        url: `/shared_lists/${sharedListId}/signup_forms/${formId}`,
        method: 'PUT',
        data,
        networkId: collectionId,
      }),
      transformResponse: (response) => deserialize(response),
      invalidatesTags: (_, __, { sharedListId }) => [{ type: 'signup-form', id: sharedListId }],
      async onQueryStarted({ collectionId, sharedListId, ...data }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          signupFormsApi.util.updateQueryData('getSignupForm', { collectionId, sharedListId }, (draft) => {
            Object.assign(draft, data);
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),

    setSignupFormActive: builder.mutation({
      query: ({ collectionId, sharedListId, formId, active }) => ({
        url: `/shared_lists/${sharedListId}/signup_forms/${formId}`,
        method: 'PUT',
        data: { active },
        networkId: collectionId,
      }),
      transformResponse: (response) => deserialize(response),
      invalidatesTags: (_, __, { sharedListId }) => [{ type: 'signup-form', id: sharedListId }],
      async onQueryStarted({ sharedListId, active }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          contactsApi.util.updateQueryData('getSharedList', { listId: sharedListId.toString() }, (draft) => {
            if (draft?.sharedList) {
              Object.assign(draft.sharedList, { signupFormEnabled: active });
            }
          }),
        );

        try {
          await queryFulfilled;
          dispatch(contactsApi.util.invalidateTags(['contacts-shared-list']));
        } catch {
          patchResult.undo();
        }
      },
    }),

    deleteSignupForm: builder.mutation({
      query: ({ collectionId, sharedListId, formId }) => ({
        url: `/shared_lists/${sharedListId}/signup_forms/${formId}`,
        method: 'DELETE',
        networkId: collectionId,
      }),
      invalidatesTags: (_, __, { sharedListId }) => [{ type: 'signup-form', id: sharedListId }],
    }),
  }),
});

export const {
  useGetSignupFormQuery,
  useCreateSignupFormMutation,
  useUpdateSignupFormMutation,
  useSetSignupFormActiveMutation,
  useDeleteSignupFormMutation,
} = signupFormsApi;

export const { useGetPublicSignupFormQuery, useSubmitPublicSignupFormMutation } = publicSignupFormsApi;
